import { Link, Navigate, useLocation } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useMe } from "@/api/useMe";

/**
 * Build the sign-in URL, appending `?next=…` when SidebarLayout has bounced
 * us here with a target path the user was trying to reach. Only relative
 * paths starting with "/" are honoured — anything else (absolute URLs,
 * protocol-relative, javascript:, etc.) is dropped on the floor.
 */
function buildSignInHref(from: unknown): string {
  if (typeof from !== "string") return "/auth/signin";
  // Reject anything that isn't a plain, same-origin absolute path. Also
  // reject `//evil.example` which technically starts with "/".
  if (!from.startsWith("/") || from.startsWith("//")) return "/auth/signin";
  return `/auth/signin?next=${encodeURIComponent(from)}`;
}

/**
 * Public landing page — shown at "/" for anonymous visitors.
 *
 * If anyone is already signed in (Firebase user OR dev persona set in
 * localStorage), we send them straight to /catalogue.
 *
 * Cura logo sits on a soft NHS-blue plinth top-left (matches Cura Core's
 * treatment in its dark header). RDi Evolve attribution lives at the
 * bottom, mirroring the in-app footer — single source of truth.
 */
export function LandingPage() {
  const { user, ready, devUser } = useAuth();
  const location = useLocation();
  const isAuthed = Boolean(user || devUser);
  // Only fetch /me when we actually have an identity to look up; useMe handles
  // 401 gracefully so it's safe either way, but this avoids the noise.
  const me = useMe();

  // Resume-after-signin: SidebarLayout bounces unauth visitors here with
  // `state.from` set to the path they were trying to reach. Pass it on to
  // the sign-in flow as a `?next=…` query string.
  const fromPath = (location.state as { from?: string } | null)?.from;
  const signInHref = buildSignInHref(fromPath);

  // Don't flicker the landing during the brief auth check.
  if (!ready) return null;

  if (isAuthed) {
    // Hold redirect briefly while /me resolves so we can route admins to
    // /admin/clients and everyone else to /catalogue. Worst case: 100ms blank.
    if (me.isLoading) {
      return (
        <div className="grid min-h-screen place-items-center bg-bg-1 text-fg-3">
          Signing you in…
        </div>
      );
    }
    // If we arrived via a bounce, honour the original target.
    const safeFrom =
      typeof fromPath === "string" && fromPath.startsWith("/") && !fromPath.startsWith("//")
        ? fromPath
        : null;
    // Role-aware landing: STUDIO_ADMIN → admin surface, CLIENT_ADMIN →
    // their team management page, everyone else → catalogue. `?next=` from
    // a bounced sign-in always wins.
    const role = me.data?.user?.role;
    const roleLanding =
      role === "STUDIO_ADMIN"
        ? "/admin/clients"
        : role === "CLIENT_ADMIN"
          ? "/settings/team"
          : "/catalogue";
    const target = safeFrom ?? roleLanding;
    return <Navigate to={target} replace />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg-1">
      <header className="flex items-center justify-between px-8 py-5">
        <span
          className="inline-flex items-center rounded-md px-2.5 py-1.5"
          style={{ background: "var(--brand-primary)" }}
          aria-label="+CURA"
        >
          <img src="/cura-logo.svg" alt="+CURA" className="h-4 brightness-0 invert" />
        </span>
        <Link
          to={signInHref}
          className="text-[13px] font-semibold text-fg-1 hover:text-[color:var(--brand-link)]"
        >
          Sign in →
        </Link>
      </header>

      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col items-center justify-center px-8 pb-16 pt-8 text-center">
        <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--studio-border,var(--studio-tint))] bg-[var(--studio-wash)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--studio-dark)]">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--studio)" }} />
          Part of the +CURA family
        </span>

        <h1 className="m-0 mb-4 text-[44px] font-bold leading-[1.05] tracking-tight text-fg-1 sm:text-[56px]">
          Branded test kits,
          <br />
          <span style={{ color: "var(--studio)" }}>ordered in minutes.</span>
        </h1>
        <p className="mx-auto m-0 mb-10 max-w-2xl text-[17px] leading-relaxed text-fg-3 sm:text-[18px]">
          +CURA Studio lets GP practices and care providers self-serve their own
          branded self-test kits. Pick a kit from the +CURA catalogue, add your
          logo and colours, approve the proof, place the order. We handle print,
          fulfilment, and the lab return.
        </p>

        <div className="mb-12 grid w-full max-w-2xl gap-3 sm:grid-cols-2">
          <Link
            to={signInHref}
            className="group flex flex-col gap-1.5 rounded-lg border border-[var(--border-1)] bg-white p-5 text-left transition-shadow hover:shadow-md"
          >
            <span
              className="inline-block h-1.5 w-6 rounded-full"
              style={{ background: "var(--studio)" }}
              aria-hidden
            />
            <span className="text-[15px] font-semibold text-fg-1">
              Practice manager sign-in
            </span>
            <span className="text-[13px] text-fg-3">
              Order kits for your practice or PCN. We'll email you a magic link —
              no password to remember.
            </span>
          </Link>

          <Link
            to={signInHref}
            className="group flex flex-col gap-1.5 rounded-lg border border-[var(--border-1)] bg-white p-5 text-left transition-shadow hover:shadow-md"
          >
            <span
              className="inline-block h-1.5 w-6 rounded-full"
              style={{ background: "var(--admin)" }}
              aria-hidden
            />
            <span className="text-[15px] font-semibold text-fg-1">
              RDi admin sign-in
            </span>
            <span className="text-[13px] text-fg-3">
              Cross-tenant operations: clients, catalogue, hold queue, audit log.
              Signed-in admins land on the Studio Admin surface.
            </span>
          </Link>
        </div>

        <ul
          data-chrome
          className="grid w-full max-w-3xl gap-x-6 gap-y-3 text-left text-[14px] text-fg-2 sm:grid-cols-3"
        >
          {[
            ["Upload, see, approve", "Live preview of every printed artefact before you commit."],
            ["NHS-grade typography", "NHS UK design system underpins every screen."],
            ["Contract proof, on file", "Each order keeps a hashed PDF of exactly what shipped."],
            ["Locked clinical content", "Return address + kit code stay UKAS-correct, always."],
            ["Audit-trailed", "Every change chained, exportable, ISO 27001 / SOC 2 friendly."],
            ["Per-client storefront", "Admins decide which kits each tenant can see."],
          ].map(([title, body]) => (
            <li key={title} className="flex gap-2.5 rounded-md bg-white/60 p-3 backdrop-blur-sm">
              <span
                className="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
                style={{ background: "var(--studio)" }}
                aria-hidden
              />
              <div>
                <div className="font-semibold text-fg-1">{title}</div>
                <div className="text-[13px] text-fg-3">{body}</div>
              </div>
            </li>
          ))}
        </ul>
      </main>

      <footer className="border-t border-[var(--border-1)] bg-white px-8 py-6 text-center text-[12px] text-fg-3">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-2 sm:flex-row sm:justify-between">
          <span>
            &copy; RDi Evolve {new Date().getFullYear()} · +CURA Studio is part of the
            +CURA family of NHS-facing healthcare products.
          </span>
          <a
            href="https://thisisrdi.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-fg-2 hover:text-fg-1"
          >
            Evolve <span className="px-1 text-fg-mute">|</span> thisisrdi.com
          </a>
        </div>
      </footer>
    </div>
  );
}
